import type { FieldDiff } from "../../../lib/columnVersionDiff";
import { Badge } from "../../ui/badge";

interface FieldDiffRowProps {
  fieldDiff: FieldDiff;
}

/**
 * Renders one field row in the column version comparison.
 *
 * The same `segments` list is shown on both sides, filtered per pane:
 *   left  (older version) — eq + del; del rendered as red strikethrough
 *   right (current)       — eq + add; add rendered as green
 */
export function FieldDiffRow({ fieldDiff }: FieldDiffRowProps) {
  const { label, status, segments } = fieldDiff;

  const leftSegments = segments.filter(
    (s) => s.type === "eq" || s.type === "del",
  );
  const rightSegments = segments.filter(
    (s) => s.type === "eq" || s.type === "add",
  );

  const badgeVariant = status === "changed" ? "destructive" : "secondary";
  const badgeLabel = status === "changed" ? "Changed" : "Unchanged";

  // Shared border classes from the app's Input/Textarea components
  const fieldBoxClasses =
    "w-full min-h-[60px] bg-white px-3 py-2 text-sm ring-1 ring-inset ring-stone-300 break-words whitespace-pre-wrap";

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-stone-700">{label}</span>
        <Badge variant={badgeVariant} className="text-xs px-1.5 py-0">
          {badgeLabel}
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {/* Left pane — older version: eq (plain) + del (red strikethrough) */}
        <div className={fieldBoxClasses}>
          {leftSegments.length === 0 ? (
            <span className="text-stone-400 italic">empty</span>
          ) : (
            leftSegments.map((segment, i) =>
              segment.type === "del" ? (
                <span key={i} className="text-red-600 line-through">
                  {segment.text}
                </span>
              ) : (
                <span key={i}>{segment.text}</span>
              ),
            )
          )}
        </div>

        {/* Right pane — current version: eq (plain) + add (green) */}
        <div className={fieldBoxClasses}>
          {rightSegments.length === 0 ? (
            <span className="text-stone-400 italic">empty</span>
          ) : (
            rightSegments.map((segment, i) =>
              segment.type === "add" ? (
                <span key={i} className="text-green-700 bg-green-50">
                  {segment.text}
                </span>
              ) : (
                <span key={i}>{segment.text}</span>
              ),
            )
          )}
        </div>
      </div>
    </div>
  );
}
